import { useCallback, useMemo, useState } from "react";

import { Button, Icon_Kebab, Menu, useManagedModal } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import toast from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";

import { webAppURL } from "../../config";
import type { UserID } from "../../hooks/useAccount";
import { User } from "../../hooks/useProject";
import { useTracker } from "../../hooks/useTracker";
import type { trackingAdapter } from "../../lib/TrackingAdapter";
import { SharingSpace } from "../../types";
import { tabletSmallBreakpoint } from "../../utils/constants";
import { formatDateCompressed } from "../../utils/helpers";
import { MenuPortal } from "../Headers/components/ShareModal/components/MenuPortal";
import { UserName } from "../UserName/UserName";

import style from "./LinkList.module.css";

const copyToClipboard = (text: string) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      toast.success("Copied to clipboard");
    })
    .catch(() => {
      toast.error("Failed to copy link to clipboard");
    });
};

export type Link = {
  title: string;
  type: "view_link" | "project_page_link";
  id: string;
  space?: SharingSpace;
  location?: string;
  uploadedAt: Date;
  uploadedByUserId?: UserID;
  relativeURL: string;
};

export type LinkListTelemetryLocation = Parameters<
  typeof trackingAdapter.trackLinkClicked
>[0]["location"];

interface LinkListProps {
  links: Link[];
  users: User[];
  isShortVersion?: boolean;
  telemetryLocation: LinkListTelemetryLocation;
  showPromo: boolean;
  promoComponent: React.ReactNode;
  UnpublishModalComponent?: React.ComponentType<{
    onClose: () => void;
    open: boolean;
    viewLinkId: string;
    telemetryLocation: LinkListTelemetryLocation;
  }>;
  hideLocation?: boolean;
}

export const LinkList: React.FC<LinkListProps> = ({
  links,
  users,
  isShortVersion,
  showPromo,
  promoComponent,
  telemetryLocation,
  UnpublishModalComponent,
  hideLocation: hideLocationInput,
}) => {
  const { teamID } = useParams();
  const tracker = useTracker();
  const navigate = useNavigate();

  const linkTelemetryHelper = useCallback(
    (link: Link) => ({
      activeTeamId: teamID,
      type: link.type,
      ...(link.type === "view_link" && { viewLinkId: link.id }),
      ...(link.type === "project_page_link" && { projectId: link.id }),
      location: telemetryLocation,
    }),
    [telemetryLocation, teamID]
  );

  const [unpublishViewLinkId, setUnpublishViewLinkId] = useState<string | null>(null);
  const unpublishModal = useManagedModal();

  const mappedLinks = useMemo(() => {
    return links.map((viewLink) => {
      const user = users.find((user) => user.userId === viewLink.uploadedByUserId);
      return {
        ...viewLink,
        uploadedByUser: user,
      };
    });
  }, [links, users]);

  const hideLocation = isShortVersion || hideLocationInput;

  return (
    <>
      {mappedLinks.length > 0 && (
        <div>
          <div
            className={classNames(
              style.gridRow,
              style.header,
              isShortVersion ? style.gidRowShort : "",
              hideLocation ? style.gridRowHideLocation : ""
            )}
          >
            {!isShortVersion && <div className={style.columnProject}>Project</div>}
            {!isShortVersion && <div className={style.columnProjectName}>Project name</div>}
            <div className={style.columnLink}>Link</div>
            <div className={style.columnCopy}></div>
            {!hideLocation && <div className={style.columnLocation}>Location</div>}
            <div className={style.columnUpdatedAt}>Last updated</div>
            <div className={style.columnUpdatedBy}>Updated by</div>
            <div className={style.columnExtra}></div>
          </div>

          <div>
            {mappedLinks.map((link) => (
              <div className={style.rowWrapper} key={link.id} data-testid="view-link-row">
                <Link
                  to={link.relativeURL}
                  className={style.linkOverlay}
                  onClick={(e) => {
                    // avoid make the whole row clickable for mobile
                    if (window.innerWidth > tabletSmallBreakpoint) {
                      tracker.trackLinkClicked(linkTelemetryHelper(link));
                    } else {
                      e.preventDefault();
                    }
                  }}
                />
                <div
                  className={classNames(
                    style.gridRow,
                    style.row,
                    isShortVersion ? style.gidRowShort : "",
                    hideLocation ? style.gridRowHideLocation : ""
                  )}
                >
                  {!isShortVersion && (
                    <>
                      <div className={style.columnProjectName}>
                        <div>
                          <b>{link.title}</b>
                        </div>
                      </div>

                      <div className={style.columnProject}>
                        <b>{link.title}</b>
                        <div className={style.subtitle}>
                          {`${formatDateCompressed(link.uploadedAt.toString())} · `}{" "}
                          <UserName user={link.uploadedByUser} fallback="-" />
                        </div>
                      </div>
                    </>
                  )}

                  <div
                    className={classNames([style.link, style.columnLink])}
                    title={`${webAppURL}${link.relativeURL}`}
                  >
                    <div className={style.url}>{`${webAppURL}${link.relativeURL}`}</div>
                  </div>

                  <div className={style.columnCopy}>
                    <Button
                      variant="borderless"
                      onClick={(e) => {
                        e.preventDefault();
                        tracker.trackLinkCopied(linkTelemetryHelper(link));
                        copyToClipboard(`${webAppURL}${link.relativeURL}`);
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                  {!hideLocation && <div className={style.columnLocation}>{link.location}</div>}

                  <div
                    className={classNames([style.date, style.columnUpdatedAt])}
                    title={formatDateCompressed(link.uploadedAt.toString())}
                  >
                    {formatDateCompressed(link.uploadedAt.toString())}
                  </div>
                  <div className={classNames([style.name, style.columnUpdatedBy])}>
                    <UserName user={link.uploadedByUser} fallback="-" />
                  </div>

                  <div className={classNames([style.columnExtra])}>
                    <MenuPortal
                      activator={
                        <Button
                          variant="borderless"
                          onClick={(e) => {
                            e.preventDefault();
                          }}
                        >
                          <Icon_Kebab />
                        </Button>
                      }
                      className={style.contextMenu}
                      onClick={(e) => {
                        e.preventDefault();
                      }}
                      onOpen={() => {
                        tracker.trackLinkContextMenuOpened(linkTelemetryHelper(link));
                      }}
                    >
                      <Menu.Item
                        closeOnClick
                        onClick={() => {
                          tracker.trackLinkContextMenuActed({
                            ...linkTelemetryHelper(link),
                            action: "open_link",
                          });
                          navigate(link.relativeURL);
                        }}
                      >
                        <span>Open</span>
                      </Menu.Item>
                      <Menu.Item
                        onClick={(e) => {
                          e.stopPropagation();
                          tracker.trackLinkContextMenuActed({
                            ...linkTelemetryHelper(link),
                            action: "open_link_in_new_tab",
                          });
                          window.open(window.location.origin + link.relativeURL, "_blank");
                        }}
                      >
                        <span>Open in new tab</span>
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item
                        closeOnClick
                        onClick={() => {
                          tracker.trackLinkContextMenuActed({
                            ...linkTelemetryHelper(link),
                            action: "copy_link",
                          });
                          copyToClipboard(`${webAppURL}${link.relativeURL}`);
                        }}
                      >
                        <span>Copy</span>
                      </Menu.Item>
                      {UnpublishModalComponent && (
                        <>
                          <Menu.Divider />
                          <Menu.Item
                            disabled={link.space === undefined}
                            closeOnClick
                            onClick={() => {
                              tracker.trackLinkContextMenuActed({
                                ...linkTelemetryHelper(link),
                                action: "unpublish",
                              });
                              setUnpublishViewLinkId(link.id);
                              unpublishModal.open();
                            }}
                          >
                            <span className={style.alertButton}>Unpublish...</span>
                          </Menu.Item>
                        </>
                      )}
                    </MenuPortal>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {showPromo && <>{promoComponent}</>}
      {UnpublishModalComponent && (
        <UnpublishModalComponent
          open={unpublishModal.isOpen}
          onClose={unpublishModal.close}
          viewLinkId={unpublishViewLinkId ?? ""}
          telemetryLocation={telemetryLocation}
        />
      )}
    </>
  );
};
